import React from 'react';
import { Tooltip, Typography, useTheme } from '@mui/material';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import {
  CallScribeSentimentGate,
  CallScribeSpeaker,
  ICallScribeParticipant,
  ISentimentJudgment,
  SentimentJudgmentSpeakerName,
} from 'services/callScribe/types';
import { SPEAKER_LABEL, laneLabel } from 'services/callScribe/participants';
import { CUE_LABELS, describeGateCaption } from './emotionTimelineModel';
import {
  EMOTION_STRENGTH,
  SPECTRUM_EMOTIONS,
  STRENGTH_WORDS,
  SpectrumSegment,
  buildAxisTicks,
  buildEmotionStory,
  buildLaneSegments,
  buildSpanSummary,
  buildTriggerFlags,
  formatClock,
  thinTicks,
} from './emotionSpectrumModel';
import useEmotionSpectrumStyles, { emotionColor } from './emotionSpectrumStyles';

interface Props {
  judgment: ISentimentJudgment | null;
  durationMs: number;
  gate?: CallScribeSentimentGate | null;
  onSelectTurn?: (turn: { startMs: number; recordingMs: number }) => void;
  participants?: ICallScribeParticipant[];
}

const MONO_CAPTION = 'Technician lane unavailable (single-channel recording)';

const LANE_ROLES: Array<{ speaker: SentimentJudgmentSpeakerName; role: CallScribeSpeaker }> = [
  { speaker: 'customer', role: CallScribeSpeaker.Customer },
  { speaker: 'technician', role: CallScribeSpeaker.Technician },
];

const EmotionSpectrum: React.FC<Props> = ({ judgment, durationMs, gate, onSelectTurn, participants }) => {
  const classes = useEmotionSpectrumStyles();
  const theme = useTheme();

  if (!judgment || judgment.turns.length === 0 || durationMs <= 0) {
    return (
      <div className={classes.empty}>
        <span className={classes.emptyRing} aria-hidden="true">
          <InsightsRoundedIcon />
        </span>
        <p className={classes.emptyText}>Emotion analysis is not available for this call</p>
      </div>
    );
  }

  const pct = (ms: number): string => `${(ms / durationMs) * 100}%`;
  const describe = (s: SpectrumSegment): string => {
    const cueText = s.cues.length ? ` · ${s.cues.map(c => CUE_LABELS[c] ?? c).join(', ')}` : '';
    return `${formatClock(s.startMs)}–${formatClock(s.endMs)} · ${s.emotion} (${STRENGTH_WORDS[s.intensity] ?? 'strong'}) · "${s.quote}"${cueText}`;
  };
  const select = (s: SpectrumSegment): void => {
    const turn = judgment.turns.find(t => t.turnId === s.turnId);
    if (turn && onSelectTurn) onSelectTurn({ startMs: turn.startMs, recordingMs: s.startMs });
  };
  const flags = buildTriggerFlags(judgment);
  const isMono = judgment.channelMode === 'mono';
  const allLanes = LANE_ROLES.map(lane => ({ ...lane, label: laneLabel(lane.role, participants) }));
  const lanes = isMono ? allLanes.filter(lane => lane.speaker === 'customer') : allLanes;
  const labelOf = (speaker: SentimentJudgmentSpeakerName): string =>
    allLanes.find(lane => lane.speaker === speaker)?.label ?? speaker;
  const gateCaption = describeGateCaption(gate);
  const story = buildEmotionStory(judgment, durationMs, 'customer');
  const laneStories = lanes
    .map(lane => `${lane.label}: ${buildEmotionStory(judgment, durationMs, lane.speaker) || 'no turns'}`)
    .join('. ');
  const chartLabel = `Emotion across the call. ${laneStories}.`;
  const chartRole = onSelectTurn || flags.length > 0 ? 'group' : 'img';

  return (
    <div className={classes.root}>
      {gateCaption ? <Typography className={classes.unavailable}>{gateCaption}</Typography> : null}
      {story ? <p className={classes.story}>{story}</p> : null}
      <div role={chartRole} aria-label={chartLabel} className={classes.chart}>
        {lanes.map(lane => (
          <div key={lane.speaker} className={classes.lane}>
            <span className={classes.laneLabel}>{lane.label}</span>
            <div className={classes.track}>
              {buildLaneSegments(judgment, lane.speaker, durationMs).map(s => {
                const style = { left: pct(s.startMs), width: pct(s.endMs - s.startMs), background: emotionColor(theme, s.emotion), opacity: s.strength };
                if (s.kind === 'carry') {
                  return <span key={`${s.turnId}-carry`} aria-hidden className={`${classes.segment} ${classes.carry}`} style={style} />;
                }
                const namePrefix = lane.label === SPEAKER_LABEL[lane.role] ? '' : `${lane.label}: `;
                const label = `${namePrefix}${describe(s)}${onSelectTurn ? `. Play from ${formatClock(s.startMs)}` : ''}`;
                return (
                  <Tooltip key={s.turnId} title={describe(s)} describeChild>
                    {onSelectTurn ? (
                      <button
                        type="button"
                        aria-label={label}
                        className={`${classes.segment} ${classes.clickable}`}
                        style={style}
                        onClick={() => { select(s); }}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(s); } }}
                      />
                    ) : (
                      <span role="img" aria-label={label} className={classes.segment} style={style} />
                    )}
                  </Tooltip>
                );
              })}
              {lane.speaker === 'customer' && flags.map(f => (
                <Tooltip key={f.trigger} title={`${f.label} · "${f.quote}"`} describeChild>
                  <span
                    role="img"
                    tabIndex={0}
                    aria-label={`Escalation trigger: ${f.label} · "${f.quote}"`}
                    className={classes.flag}
                    style={{ left: pct(f.atMs) }}
                  />
                </Tooltip>
              ))}
            </div>
          </div>
        ))}
        <div className={classes.axis} aria-hidden>
          {thinTicks(buildAxisTicks(durationMs), 5).map(t => (
            <span key={t.ms} className={`${classes.tick} ${t.isEnd ? classes.tickEnd : ''} ${t.ms === 0 ? classes.tickStart : ''}`} style={{ left: pct(t.ms) }}>{t.label}</span>
          ))}
        </div>
      </div>
      {isMono ? <Typography className={classes.unavailable}>{MONO_CAPTION}</Typography> : null}
      <div className={classes.summary}>
        <span><strong>{labelOf('customer')}:</strong> {buildSpanSummary(judgment, 'customer', durationMs) || '—'}</span>
        {isMono ? null : (
          <span><strong>{labelOf('technician')}:</strong> {buildSpanSummary(judgment, 'technician', durationMs) || '—'}</span>
        )}
      </div>
      <div className={classes.legend}>
        {SPECTRUM_EMOTIONS.map(e => (
          <span key={e}><i className={classes.swatch} style={{ background: emotionColor(theme, e) }} />{e}</span>
        ))}
        <span>
          Strength{' '}
          {[0, 1, 2, 3].map(i => (
            <i key={i} className={classes.swatch} style={{ background: emotionColor(theme, 'Frustrated'), opacity: EMOTION_STRENGTH[i] }} />
          ))}
        </span>
      </div>
    </div>
  );
};

export default EmotionSpectrum;
